import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { Button } from './ui/button';

const HeroSection = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32 pb-20 bg-white">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1920&h=1080&fit=crop)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#c4a35a]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#c4a35a]/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              <span className="text-[#1a1d2e]">{t.heroTitle}</span>
              {t.heroSubtitle && (
                <>
                  <br />
                  <span className="text-[#c4a35a]">{t.heroSubtitle.split('-')[1] || t.heroSubtitle}</span>
                </>
              )}
            </h1>

            <p className="text-gray-600 text-lg leading-relaxed max-w-2xl">
              {t.heroDescription}
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-[#c4a35a] text-[#0f1117] hover:bg-[#d4b36a] font-medium px-8 py-6 text-lg group"
              >
                {t.letsTalk}
                <ArrowRight className="ms-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={scrollToContact}
                size="lg"
                variant="outline"
                className="border-[#c4a35a] text-[#c4a35a] hover:bg-[#c4a35a]/10 font-medium px-8 py-6 text-lg"
              >
                {t.bookConsultation}
              </Button>
            </div>

            {/* Certificates */}
            <div className="grid grid-cols-3 gap-4 pt-8">
              <div className="relative group">
                <img
                  src="/cert1.jpg"
                  alt="Kurdistan Bar Association Certificate"
                  className="w-full h-auto rounded-lg shadow-lg border-2 border-[#c4a35a]/20 hover:border-[#c4a35a]/60 transition-all duration-300"
                />
              </div>
              <div className="relative group">
                <img
                  src="/cert2.jpg"
                  alt="Company Establishment Certificate Kurdish"
                  className="w-full h-auto rounded-lg shadow-lg border-2 border-[#c4a35a]/20 hover:border-[#c4a35a]/60 transition-all duration-300"
                />
              </div>
              <div className="relative group">
                <img
                  src="/cert3.jpg"
                  alt="Company Establishment Certificate Arabic"
                  className="w-full h-auto rounded-lg shadow-lg border-2 border-[#c4a35a]/20 hover:border-[#c4a35a]/60 transition-all duration-300"
                />
              </div>
            </div>
          </div>

          {/* Image/Visual */}
          <div className="relative">
            <div className="relative w-full aspect-square max-w-md mx-auto">
              {/* Circular background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#c4a35a]/20 to-transparent rounded-full" />
              <div className="absolute inset-4 border-2 border-[#c4a35a]/30 rounded-full" />
              <div className="absolute inset-8 border border-[#c4a35a]/20 rounded-full" />
              
              {/* Logo in center */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-96 h-96 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full border-4 border-[#c4a35a]/40 shadow-2xl">
                  <img
                    src="/logo.png"
                    alt="Noble Law Firm"
                    className="w-88 h-88 object-contain rounded-full"
                  />
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute top-10 left-0 w-16 h-16 bg-[#c4a35a]/20 rounded-lg backdrop-blur-sm flex items-center justify-center animate-float">
                <span className="text-2xl">🏛️</span>
              </div>
              <div className="absolute bottom-20 right-0 w-16 h-16 bg-[#c4a35a]/20 rounded-lg backdrop-blur-sm flex items-center justify-center animate-float" style={{animationDelay: '1s'}}>
                <span className="text-2xl">⚖️</span>
              </div>
              <div className="absolute top-1/2 left-0 w-12 h-12 bg-[#c4a35a]/20 rounded-full backdrop-blur-sm flex items-center justify-center animate-float" style={{animationDelay: '2s'}}>
                <span className="text-xl">💼</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#c4a35a]/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-[#c4a35a] rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
